import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ChartExtendedContent, ChartContent, ChartExtendedMetaInfo } from './entities';
import { ChartConfigRestful } from './restful';
import { Session, UUID } from '@benzinga/session';
import { ListenableSubscribable } from '@benzinga/subscribable';

export interface ChartConfigGetChartData {
  chart: ChartContent | ChartExtendedContent | null;
  type: 'chart-config:get_chart_data';
}

interface ChartConfigPutPurchaseErrorEvent {
  uuid: UUID | null;
  type: 'chart-config:put_chart_data';
}

interface ChartConfigPostPurchaseErrorEvent {
  uuid: UUID | null;
  type: 'chart-config:post_chart_data';
}

interface ChartConfigDeletePurchaseErrorEvent {
  guid: UUID | number;
  type: 'chart-config:delete_chart_data';
}

interface ChartConfigGetChartsPurchaseErrorEvent {
  charts: ChartExtendedMetaInfo[];
  type: 'chart-config:get_charts';
}

interface ErrorEvent {
  error: SafeError;
  errorType:
    | 'chart-config:delete_chart_data'
    | 'chart-config:get_chart_data'
    | 'chart-config:put_chart_data'
    | 'chart-config:post_chart_data'
    | 'chart-config:get_charts';
  type: 'error';
}

export type ChartConfigRestfulEvent =
  | ChartConfigGetChartData
  | ChartConfigPutPurchaseErrorEvent
  | ChartConfigPostPurchaseErrorEvent
  | ChartConfigDeletePurchaseErrorEvent
  | ChartConfigGetChartsPurchaseErrorEvent
  | ErrorEvent;

export class ChartConfigRequest extends ListenableSubscribable<ChartConfigRestfulEvent> {
  private restful: ChartConfigRestful;

  constructor(session: Session) {
    super();
    this.restful = new ChartConfigRestful(session);
  }

  public getChartData = async (chartGuid: UUID | number | null): SafePromise<ChartExtendedContent | null> => {
    const response = await this.restful.getChartData(chartGuid);
    if (response?.err) {
      this.dispatch({
        error: response?.err || new SafeError('ChartConfigVerifyPurchase call failed', 'chart-config:get_chart_data'),
        errorType: 'chart-config:get_chart_data',
        type: 'error',
      });
    } else {
      this.dispatch({
        chart: response?.ok,
        type: 'chart-config:get_chart_data',
      });
    }
    return response;
  };

  public putChartData = async (
    chartGuid: UUID | null,
    chartState: ChartExtendedContent,
  ): SafePromise<{ guid: UUID } | null> => {
    const response = await this.restful.putChartData(chartGuid, chartState);
    if (response?.err) {
      this.dispatch({
        error: response?.err || new SafeError('ChartConfigReportPurchase call failed', 'chart-config:put_chart_data'),
        errorType: 'chart-config:put_chart_data',
        type: 'error',
      });
    } else {
      this.dispatch({
        type: 'chart-config:put_chart_data',
        uuid: response?.ok?.guid ?? null,
      });
    }
    return response;
  };

  public postChartData = async (chart: Omit<ChartExtendedContent, 'guid'>): SafePromise<{ guid: UUID } | null> => {
    const response = await this.restful.postChartData(chart);
    if (response?.err) {
      this.dispatch({
        error: response?.err || new SafeError('ChartConfigReportPurchase call failed', 'chart-config:post_chart_data'),
        errorType: 'chart-config:post_chart_data',
        type: 'error',
      });
    } else {
      this.dispatch({
        type: 'chart-config:post_chart_data',
        uuid: response?.ok?.guid ?? null,
      });
    }
    return response;
  };

  public deleteChartData = async (guid: UUID | number): SafePromise<Response> => {
    const response = await this.restful.deleteChartData(guid);
    if (response?.err) {
      this.dispatch({
        error:
          response?.err || new SafeError('ChartConfigReportPurchase call failed', 'chart-config:delete_chart_data'),
        errorType: 'chart-config:delete_chart_data',
        type: 'error',
      });
    } else {
      this.dispatch({
        guid,
        type: 'chart-config:delete_chart_data',
      });
    }
    return response;
  };

  public getCharts = async (): SafePromise<ChartExtendedMetaInfo[]> => {
    const response = await this.restful.getCharts();
    if (response?.err) {
      this.dispatch({
        error: response?.err || new SafeError('ChartConfigReportPurchase call failed', 'chart-config:get_charts'),
        errorType: 'chart-config:get_charts',
        type: 'error',
      });
      return { err: response.err };
    } else {
      this.dispatch({
        charts: response?.ok?.charts ?? [],
        type: 'chart-config:get_charts',
      });
      return { ok: response?.ok?.charts ?? [] };
    }
  };
}
